import React, { useState } from "react";
import MaterialIcon from "../../../components/MaterialIcon";
import { BASE_URL } from "../../../helper/constants";
import { convertPosition } from "../../../helper/convertor";
import { Card, Chip, EmptyState, SecondaryButton } from "./CockpitPrimitives";
import "./CockpitCompanyPanel.css";

// Company admins hold ROLE_COMPANY_SUPER_ADMIN ("COMPANY ADMIN"); ROLE_COMPANY_ADMIN is the
// role of ordinary employees ("EMPLOYEE"), so the position decides for everyone else.
function roleLabel(role, position, strings) {
  const pos = String(position || "").trim().toUpperCase();
  if (role === "ROLE_COMPANY_SUPER_ADMIN" || pos === "COMPANY ADMIN") {
    return strings.cockpit_company_role_super_admin || "Firmen-Admin";
  }
  if (role === "ROLE_COMPANY_DEPARTMENT_ADMIN" || pos === "COMPANY_DEPARTMENT_ADMIN") {
    return strings.cockpit_company_role_dept_admin || "Abteilungs-Admin";
  }
  if (!pos || pos === "EMPLOYEE") return strings.cockpit_company_role_member || "Mitarbeiter:in";
  return convertPosition(strings, position) || position;
}

function Stat({ icon, value, label, onClick }) {
  return (
    <button type="button" className="cockpit-company-stat" onClick={onClick} disabled={!onClick}>
      <span className="cockpit-company-stat-icon">
        <MaterialIcon icon={icon} size={18} color="var(--kjuup-accent)" weight={400} />
      </span>
      <span className="cockpit-company-stat-copy">
        <span className="cockpit-company-stat-value">{value}</span>
        <span className="cockpit-company-stat-label">{label}</span>
      </span>
    </button>
  );
}

/**
 * "Firma" tab for Kjuup+ / Kjuup.io+ members (the app's company page): the
 * company card with its numbers and the Firmeneinstellungen button (they hold
 * the Admin-Panel sections for company admins), then the extra cards the
 * cockpit adds.
 */
export default function CockpitCompanyPanel({
  strings,
  company,
  companies = [],
  onSelectCompany,
  internalRooms = [],
  onOpenCompanySettings,
  onOpenDocuments,
  onOpenCompanies,
  onAddCompany,
  extra,
}) {
  const [settingsBusy, setSettingsBusy] = useState(false);
  const openSettings = async () => {
    if (settingsBusy || !onOpenCompanySettings) return;
    setSettingsBusy(true);
    try {
      await onOpenCompanySettings(company);
    } finally {
      setSettingsBusy(false);
    }
  };

  if (!company) {
    return (
      <div className="cockpit-company">
      <Card>
        <EmptyState
          icon="domain"
          title={strings.cockpit_company_none || "Keine Kjuup+ Firma gefunden"}
          action={
            <SecondaryButton icon="add" onClick={onAddCompany || onOpenCompanies}>
              {strings.add_or_join_company || "Add or join company"}
            </SecondaryButton>
          }
        />
      </Card>
      </div>
    );
  }

  const roomCount = company.internalRoomCount || (Array.isArray(company.internalRooms) ? company.internalRooms.length : internalRooms.length) || 0;
  return (
    <div className="cockpit-company">
      <div className="cockpit-company-switch" role="tablist" aria-label={strings.cockpit_company_eyebrow || "Firma"}>
        {companies.map((item) => {
          const selected = String(item.id) === String(company.id);
          return (
            <button
              key={item.id}
              type="button"
              role="tab"
              aria-selected={selected}
              className={`cockpit-company-pick${selected ? " cockpit-company-pick--on" : ""}`}
              onClick={() => onSelectCompany && onSelectCompany(item.id)}
            >
              {item.logo ? (
                <img className="cockpit-company-pick-logo" src={BASE_URL + item.logo} alt="" />
              ) : (
                <span className="cockpit-company-pick-logo cockpit-company-pick-logo--fallback">
                  <MaterialIcon icon="domain" size={16} color="#737373" weight={400} />
                </span>
              )}
              <span className="cockpit-company-pick-name">{item.name}</span>
            </button>
          );
        })}
        {onAddCompany ? (
          <button type="button" className="cockpit-company-pick cockpit-company-pick--add" onClick={onAddCompany}>
            <span className="cockpit-company-pick-logo cockpit-company-pick-logo--add">
              <MaterialIcon icon="add" size={16} color="currentColor" weight={500} />
            </span>
            <span className="cockpit-company-pick-name">{strings.add_or_join_company || "Add or join company"}</span>
          </button>
        ) : null}
      </div>
      <Card className="cockpit-company-card">
        <div className="cockpit-company-head">
          {company.logo ? (
            <img className="cockpit-company-logo" src={BASE_URL + company.logo} alt={company.name} />
          ) : (
            <div className="cockpit-company-logo cockpit-company-logo--fallback">
              <MaterialIcon icon="domain" size={28} color="#737373" weight={300} />
            </div>
          )}
          <div className="cockpit-company-head-text">
            <div className="cockpit-eyebrow cockpit-eyebrow--accent">
              {company.isKjuup === false
                ? strings.cockpit_company_eyebrow || "Firma"
                : `Kjuup+ · ${strings.cockpit_company_eyebrow || "Firma"}`}
            </div>
            <h2 className="cockpit-company-name">
              {company.name}
              {company.corporateForm ? <span className="cockpit-company-form"> {company.corporateForm}</span> : null}
            </h2>
            <div className="cockpit-company-meta">
              {company.branch ? <Chip tone="slate">{company.branch}</Chip> : null}
              <Chip tone="accent">{roleLabel(company.myRole, company.myPosition, strings)}</Chip>
              {!company.isActive ? <Chip tone="red">{strings.cockpit_company_inactive || "inaktiv"}</Chip> : null}
            </div>
          </div>
          {onOpenCompanySettings ? (
            <button
              type="button"
              className={`kj-btn kj-btn--secondary kj-btn--sm cockpit-company-settings${settingsBusy ? " kj-btn--loading" : ""}`}
              onClick={openSettings}
              disabled={settingsBusy}
            >
              <MaterialIcon icon="settings" size={18} weight={400} />
              <span>{strings.cockpit_company_settings || "Firmeneinstellungen"}</span>
            </button>
          ) : null}
        </div>
        <div className="cockpit-company-stats">
          <Stat icon="group" value={company.employeeCount || 0} label={strings.cockpit_company_employees || "Mitarbeitende"} />
          <Stat
            icon="folder_open"
            value={roomCount}
            label={strings.cockpit_company_internal_rooms || "Interne Projekte"}
          />
          <Stat icon="folder_shared" value={company.documentCount || 0} label={strings.nav_company_documents || "Firmendokumente"} onClick={() => onOpenDocuments(company.id)} />
        </div>
      </Card>
      {extra}
    </div>
  );
}
